import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';

import { environment } from '@env/environment';
import { User } from '@core/entities';
import { IUser } from '@core/interfaces';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _tokenSubject: BehaviorSubject<string>;
  private _loggedInUserSubject: BehaviorSubject<User>;
  private _expirySubject: BehaviorSubject<Date>;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this._tokenSubject = new BehaviorSubject<string>(null);
    this._loggedInUserSubject = new BehaviorSubject<User>(null);
    this._expirySubject = new BehaviorSubject<Date>(new Date());

    this.setCredentials();
  }

  public get token(): string {
    return this._tokenSubject.value;
  }

  public get isLoggedIn(): boolean {
    return !!this.token && !!this.user;
  }

  public get user(): User {
    return this._loggedInUserSubject.value;
  }

  public set user(user: User) {
    if (!user || user.id !== this.user.id) {
      this.logout();
      return;
    }

    if (localStorage.getItem('probankerUser')) {
      localStorage.setItem('probankerUser', this.encrypt(JSON.stringify(user)));
    } else {
      sessionStorage.setItem('probankerUser', this.encrypt(JSON.stringify(user)));
    }

    this._loggedInUserSubject.next(user);
  }

  public get expiry(): Date {
    return this._expirySubject.value;
  }

  public get isImpersonated(): boolean {
    return (
      !!localStorage.getItem('probankerOriginalToken') ||
      !!sessionStorage.getItem('probankerOriginalToken')
    );
  }

  refreshToken() {
    if (!this.token || !this.user || !this.expiry) {
      this.clearCredentials(false);
      return null;
    }

    this.startTokenTimer(this.expiry);
  }

  signUp(user: any) {
    return this.http.post<any>(`${environment.apiUrl}users/register/`, user);
  }

  login(username: string, password: string, rememberMe: boolean) {
    return this.http
      .post<any>(
        `${environment.apiUrl}users/login/`,
        { username, password },
        { withCredentials: true }
      )
      .pipe(map(res => this.authenticate({ ...res, expiry: new Date(res.expiry) }, rememberMe)));
  }

  impersonate(id: number) {
    return this.http.post<any>(`${environment.apiUrl}users/${id}/impersonate/`, {}).pipe(
      tap(() => {
        this.stopTokenTimer();
        this.rememberOriginalCredentials();
      }),
      map(res => this.authenticate({ ...res, expiry: new Date(res.expiry) }))
    );
  }

  originate() {
    return of(true).pipe(
      tap(() => this.stopTokenTimer()),
      tap(() => this.rememberCredentialsFromOriginal()),
      tap(() => this.clearOriginalCredentials()),
      tap(() => this.setCredentials()),
      tap(() => this.startTokenTimer(this.expiry))
    );
  }

  forgotPassword(email: string) {
    return this.http.post<any>(`${environment.apiUrl}users/send-reset-password-link/`, {
      login: email,
    });
  }

  resetPassword(
    password: string,
    passwordConfirm: string,
    userId: number,
    timestamp: number,
    signature: string
  ) {
    return this.http.post<any>(`${environment.apiUrl}users/reset-password/`, {
      password,
      passwordConfirm,
      userId,
      timestamp,
      signature,
    });
  }

  activatePlayer(credentials: Params) {
    return this.http.post<any>(`${environment.apiUrl}users/verify-registration/`, credentials);
  }

  clearCredentials(navigateToLogin = true) {
    this._tokenSubject.next(null);
    this._loggedInUserSubject.next(null);
    this._expirySubject.next(null);
    this.stopTokenTimer();
    localStorage.clear();
    sessionStorage.clear();

    if (navigateToLogin) {
      this.router.navigate(['/login']);
    }
  }

  logout() {
    this.http
      .post<any>(`${environment.apiUrl}users/logout/`, {}, { withCredentials: true })
      .subscribe(() => {
        this.clearCredentials();
      });
  }

  logoutAll() {
    this.http
      .post<any>(`${environment.apiUrl}users/logoutall/`, {}, { withCredentials: true })
      .subscribe(() => {
        this.clearCredentials();
      });
  }

  encrypt(message: string) {
    return message
      ? CryptoJS.AES.encrypt(message.trim(), environment.encryptionKey).toString()
      : undefined;
  }

  decrypt(message: string) {
    return message
      ? CryptoJS.AES.decrypt(message.trim(), environment.encryptionKey).toString(CryptoJS.enc.Utf8)
      : undefined;
  }

  // helper methods
  private tokenTimeout: NodeJS.Timeout;

  private startTokenTimer(expiry: Date) {
    this.tokenTimeout = setTimeout(() => this.logout(), expiry.getTime() - Date.now());
  }

  private stopTokenTimer() {
    clearTimeout(this.tokenTimeout);
  }

  private rememberOriginalCredentials() {
    if (localStorage.getItem('probankerToken')) {
      localStorage.setItem('probankerOriginalToken', localStorage.getItem('probankerToken'));
    } else {
      sessionStorage.setItem('probankerOriginalToken', sessionStorage.getItem('probankerToken'));
    }

    if (localStorage.getItem('probankerUser')) {
      localStorage.setItem('probankerOriginalUser', localStorage.getItem('probankerUser'));
    } else {
      sessionStorage.setItem('probankerOriginalUser', sessionStorage.getItem('probankerUser'));
    }

    if (localStorage.getItem('probankerExpiry')) {
      localStorage.setItem('probankerOriginalExpiry', localStorage.getItem('probankerExpiry'));
    } else {
      sessionStorage.setItem('probankerOriginalExpiry', sessionStorage.getItem('probankerExpiry'));
    }
  }

  private rememberCredentialsFromOriginal() {
    if (localStorage.getItem('probankerToken')) {
      localStorage.setItem('probankerToken', localStorage.getItem('probankerOriginalToken'));
    } else {
      sessionStorage.setItem('probankerToken', sessionStorage.getItem('probankerOriginalToken'));
    }

    if (localStorage.getItem('probankerUser')) {
      localStorage.setItem('probankerUser', localStorage.getItem('probankerOriginalUser'));
    } else {
      sessionStorage.setItem('probankerUser', sessionStorage.getItem('probankerOriginalUser'));
    }

    if (localStorage.getItem('probankerExpiry')) {
      localStorage.setItem('probankerExpiry', localStorage.getItem('probankerOriginalExpiry'));
    } else {
      sessionStorage.setItem('probankerExpiry', sessionStorage.getItem('probankerOriginalExpiry'));
    }
  }

  private clearOriginalCredentials() {
    if (localStorage.getItem('probankerOriginalToken')) {
      localStorage.removeItem('probankerOriginalToken');
    } else {
      sessionStorage.removeItem('probankerOriginalToken');
    }

    if (localStorage.getItem('probankerOriginalUser')) {
      localStorage.removeItem('probankerOriginalUser');
    } else {
      sessionStorage.removeItem('probankerOriginalUser');
    }

    if (localStorage.getItem('probankerOriginalExpiry')) {
      localStorage.removeItem('probankerOriginalExpiry');
    } else {
      sessionStorage.removeItem('probankerOriginalExpiry');
    }
  }

  private rememberCredentials(
    res: { user: User; token: string; expiry: Date },
    rememberMe?: boolean
  ) {
    if (
      (rememberMe && rememberMe.toString() === 'true') ||
      localStorage.getItem('probankerToken')
    ) {
      localStorage.setItem('probankerToken', this.encrypt(res.token));
    } else {
      sessionStorage.setItem('probankerToken', this.encrypt(res.token));
    }

    if ((rememberMe && rememberMe.toString() === 'true') || localStorage.getItem('probankerUser')) {
      localStorage.setItem('probankerUser', this.encrypt(JSON.stringify(res.user)));
    } else {
      sessionStorage.setItem('probankerUser', this.encrypt(JSON.stringify(res.user)));
    }

    if (
      (rememberMe && rememberMe.toString() === 'true') ||
      localStorage.getItem('probankerExpiry')
    ) {
      localStorage.setItem('probankerExpiry', this.encrypt(res.expiry.toString()));
    } else {
      sessionStorage.setItem('probankerExpiry', this.encrypt(res.expiry.toString()));
    }
  }

  private setCredentials() {
    const token = this.decrypt(
      localStorage.getItem('probankerToken')
        ? localStorage.getItem('probankerToken')
        : sessionStorage.getItem('probankerToken')
    );

    this._tokenSubject.next(token);

    const user = this.decrypt(
      localStorage.getItem('probankerUser')
        ? localStorage.getItem('probankerUser')
        : sessionStorage.getItem('probankerUser')
    );

    this._loggedInUserSubject.next(user ? new User(JSON.parse(user)?.props) : null);

    const expiry = this.decrypt(
      localStorage.getItem('probankerExpiry')
        ? localStorage.getItem('probankerExpiry')
        : sessionStorage.getItem('probankerExpiry')
    );

    this._expirySubject.next(expiry ? new Date(expiry) : null);
  }

  private authenticate(res: { user: IUser; token: string; expiry: Date }, rememberMe?: boolean) {
    const user: User = new User(res.user);

    this._tokenSubject.next(res.token);
    this._loggedInUserSubject.next(user);
    this._expirySubject.next(res.expiry);

    this.startTokenTimer(res.expiry);
    this.rememberCredentials({ ...res, user }, rememberMe);

    return user;
  }
}
